import {
  useParkedRideBookingsGetCountHook,
  useParkedRideBookingsGetListHook,
} from 'api/parked-ride-bookings/parked-ride-bookings';
import { ParkedRideBookingModel } from 'api/model';
import { DefaultWebAdminPolicy, isAuthorized } from 'constants/Roles';
import useAuthenticationState from 'contexts/authentication/useAuthentication';
import useMe from 'contexts/authentication/useMe';
import useUpdateInterval from 'hooks/useUpdateInterval';
import { useCallback, useEffect, useMemo } from 'react';
import { atom, useRecoilState, useSetRecoilState } from 'recoil';
import { RequestStatus, useApiCallFn } from 'hooks/useApiCallFn';

const UPDATE_INTERVAL = 10 * 1000;

const rideWaitingListAtom = atom<ParkedRideBookingModel[] | null>({
  key: 'rideWaitingListAtom',
  default: null,
});

const rideWaitingListCountAtom = atom<number>({
  key: 'rideWaitingListCountAtom',
  default: 0,
});

// Badge-poll för NavBar — samma mönster som useRideUpdatesCountPoll ("Uppdateringar").
export const useRideWaitingListCountPoll = () => {
  const setWaitingListCount = useSetRecoilState(rideWaitingListCountAtom);

  const getWaitingListCount = useParkedRideBookingsGetCountHook();
  const getCountCall = useApiCallFn(() => getWaitingListCount());

  const {
    state: { isAuthenticated },
  } = useAuthenticationState();
  const me = useMe();

  const { startIntervalImmediately, stopInterval } = useUpdateInterval(
    async () => {
      const [count] = await getCountCall.run();
      if (!Number.isNaN(count) && count !== null) {
        setWaitingListCount(count);
      }
    },
    UPDATE_INTERVAL
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (
      isAuthenticated &&
      !!me &&
      isAuthorized(
        DefaultWebAdminPolicy.allow,
        DefaultWebAdminPolicy.deny,
        me.roles
      )
    ) {
      setTimeout(() => startIntervalImmediately(), 0);
    } else {
      stopInterval();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, me]);
};

const useRideWaitingList = () => {
  const [waitingList, setWaitingList] = useRecoilState(rideWaitingListAtom);
  const [waitingListCount, setWaitingListCount] = useRecoilState(
    rideWaitingListCountAtom
  );

  const getWaitingList = useParkedRideBookingsGetListHook();
  const getWaitingListCount = useParkedRideBookingsGetCountHook();

  const getListCall = useApiCallFn(() => getWaitingList());
  const getCountCall = useApiCallFn(() => getWaitingListCount());

  const refetchWaitingList = useCallback(async () => {
    const [list] = await getListCall.run();
    const [count] = await getCountCall.run();

    if (list) {
      setWaitingList(list);
    }
    if (!Number.isNaN(count) && count !== null) {
      setWaitingListCount(count);
    }
  }, [getListCall, getCountCall, setWaitingList, setWaitingListCount]);

  return useMemo(
    () => ({
      loading: getListCall.status === RequestStatus.Fetching,
      waitingList,
      waitingListCount,
      refetchWaitingList,
    }),
    [getListCall.status, refetchWaitingList, waitingList, waitingListCount]
  );
};

export default useRideWaitingList;
